import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
    Button,
    Card,
    Col,
    Form,
    Image,
    Modal,
    Row,
    Stack,
} from 'react-bootstrap';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { usePatientListMutation } from '../Patient/patientApi';
import { CircleAlert, FileText } from 'lucide-react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Swal from 'sweetalert2';
import config from '../../Api/config';
import EyeUpload from '../../Assets/Img/eye_upload.png';
import { getSecureItem, removeSecureItem } from "../../utils/secureStorage";
import { openFileInNewTab, getFileType } from '../../utils/FileUtils';
const Step1 = ({
    error,
    setAddEyeTestConfig,
    addEyeTestConfig,
    modalActive,
    setModalActive,
    selectedPatientAge,
    setSelectedPatientAge,
    handleChange,
    view,
    handleSelectChange,
    eyeTestImages = {},
    glass,
    imageHandle,
    images = {},
}) => {
    const location = useLocation();
    const state = location?.state || {};
    const maxDateValidation = new Date().toISOString().split('T')[0];

    // Refs for preventing re-fetching
    const fetchedRef = useRef(false);
    const addEyeTestConfigRef = useRef(addEyeTestConfig);

    // Update ref when config changes
    useEffect(() => {
        addEyeTestConfigRef.current = addEyeTestConfig;
    }, [addEyeTestConfig]);

    const [patientDetails] = usePatientListMutation();
    const user = getSecureItem('UserData') || {};
    const { t } = useTranslation();
    const posList = useSelector((state) => state.pos?.posList || []);
    const patientList = useSelector((state) => state.patient?.patientDropdownList || []);
    const opticianList = useSelector((state) => state.optician?.opticianList || []);

    const isExternal = user?.userType === 'OPTICIAN' && user?.isExternal;
    const token = getSecureItem("authToken");
    // State variables
    const [lastVisit, setLastVisit] = useState(false);
    const [opticianComment, setOpticianComment] = useState(
        addEyeTestConfig?.infoOpticianComment || ''
    );
    const [posComment, setPosComment] = useState(
        addEyeTestConfig?.infoPOSComment || ''
    );
    const [submitted, setSubmitted] = useState(false);

    // Get patient info from localStorage
    // const getPatientEmailFromStorage = useCallback(() => {
    //     const stored = getSecureItem("patientEmail");

    //     if (!stored) {
    //         removeSecureItem("patientEmail");
    //         return null;
    //     }

    //     return stored;
    // }, []);

    // const selectedPatientInformation = getPatientEmailFromStorage();

    // Use useMemo for computed options to prevent recreation on every render
    const opticianOptions = useMemo(() => {
        // Create a copy of the array to avoid mutating Redux state
        const safeOpticianList = [...(opticianList || [])];

        // Sort the copy
        return safeOpticianList
            .sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            })
            .map(item => ({
                value: item.email || '',
                label: `${item.name || ''} ${item.lastName || ''}`.trim() || item.email || '',
            }));
    }, [opticianList]);

    const posOptions = useMemo(() => {
        // Create a copy of the array
        const safePosList = [...(posList || [])];

        return safePosList.map(item => ({
            value: item.email || '',
            label: item.name || item.email || '',
        }));
    }, [posList]);

    // Set default POS for external optician
    useEffect(() => {
        if (view === 'add' && isExternal && posList?.length && !addEyeTestConfig?.posEmail) {
            setAddEyeTestConfig(prev => ({
                ...prev,
                posEmail: posList[0].email,
            }));
        }
    }, [view, isExternal, posList, addEyeTestConfig?.posEmail, setAddEyeTestConfig]);
    useEffect(() => {
        if (view === 'add' && state?.fromPatient) {
            setAddEyeTestConfig(prev => ({
                ...prev,
                patientEmail: state?.patientEmail || '',
                name: state?.name || '',
                lastName: state?.lastName || '',
            }));
        }
    }, [state, view, setAddEyeTestConfig]);
    // Set patient name from localStorage if available
    // useEffect(() => {
    //     if (selectedPatientInformation && view === 'add') {
    //         setAddEyeTestConfig(prev => ({
    //             ...prev,
    //             name: selectedPatientInformation?.name || '',
    //             lastName: selectedPatientInformation?.lastName || '',
    //             patientEmail: selectedPatientInformation?.patientEmail || '',
    //         }));
    //     }
    // }, [selectedPatientInformation, view, setAddEyeTestConfig]);

    // Fetch patient details when email changes (only for lenses)
    const fetchSelectedPatientDetails = useCallback(async (email) => {
        if (!email) return;

        // 🔁 allow re-check when patient changes
        fetchedRef.current = false;

        try {
            const { data } = await patientDetails({
                email: '',
                mobile: '',
                name: '',
                search: email,
                latestRecords: 0,
                pageSize: 50,
                pageNumber: 1,
                startDate: '',
                endDate: '',
            });

            const patient = data?.userProfileData?.[0];
            if (!patient?.dateOfBirth) return;

            const dob = new Date(patient.dateOfBirth);
            const today = new Date();

            let age = today.getFullYear() - dob.getFullYear();
            const monthDiff = today.getMonth() - dob.getMonth();
            const dayDiff = today.getDate() - dob.getDate();

            if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                age--;
            }

            // ✅ always store age
            setSelectedPatientAge(age);

            // 🚫 HARD BLOCK for contact lens tests
            if (age < 18 && !state?.glass) {
                setModalActive(true);
                return; // ⛔ stop here
            }

            // ✅ mark as fetched only if valid
            fetchedRef.current = true;

        } catch (error) {
            console.error('Error fetching patient details:', error);
        }
    }, [
        patientDetails,
        setSelectedPatientAge,
        setModalActive,
        setAddEyeTestConfig,
        state?.glass
    ]);


    // Fetch patient details when patientEmail changes (only for add view)
    useEffect(() => {
        if (
            view === 'add' &&
            addEyeTestConfig?.patientEmail &&
            !state?.glass &&
            !fetchedRef.current
        ) {
            fetchedRef.current = true;
            fetchSelectedPatientDetails(addEyeTestConfig.patientEmail);
        }
    }, [view, addEyeTestConfig?.patientEmail, state?.glass, fetchSelectedPatientDetails]);

    // Debounced search function for AsyncSelect
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    };

    const loadOptions = useCallback(debounce(async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];

        try {
            const res = await fetch(
                `${config.API_URL}/patient/NewsearchPatients`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        email: '',
                        mobile: '',
                        name: '',
                        search: inputValue,
                        latestRecords: 0,
                        pageSize: 50,
                        pageNumber: 1,
                        startDate: '',
                        endDate: '',
                    }),
                }
            );

            const data = await res.json();
            const list = data?.userProfileData || [];

            // Create a new array for the options
            return list.map((item) => ({
                value: item.email,
                label: item.email,
                name: item.name,
                lastName: item.lastName,
                dateOfBirth: item.dateOfBirth,
            }));
        } catch (error) {
            console.error('Error loading options:', error);
            return [];
        }
    }, 300), []);

    const handleCommentSubmit = async () => {
        const token = getSecureItem('authToken');
        if (
            (user?.userType === 'BACKOFFICE' || user?.userType === 'ADMIN') &&
            opticianComment.trim() === '' && posComment.trim() === ''
        ) {
            Swal.fire({
                title: 'OOPS!',
                text: t('mustEnterValue'),
                icon: 'warning',
            });
            return;
        }

        const url = `${config.API_URL}/eyetests/UpdateOpticianPOSComment`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: 'text/plain',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    eyeTestID: addEyeTestConfig?.testid,
                    opticianComment,
                    posComment,
                }),
            });

            const json = await response.json();
            if (json === true) {
                Swal.fire({
                    title: 'Done!',
                    text: 'Comment submitted successfully.',
                    icon: 'success',
                    confirmButtonColor: '#3085d6',
                });
                setSubmitted(true);
            } else {
                Swal.fire({
                    title: 'OOPS!',
                    text: t('somethingWentWrong'),
                    icon: 'error',
                });
            }
        } catch (e) {
            console.error(e);
            Swal.fire({
                title: 'OOPS!',
                text: t('somethingWentWrong'),
                icon: 'error',
            });
        }
    };

    // Find selected optician option
    const selectedOpticianOption = useMemo(() => {
        if (!addEyeTestConfig?.opticianEmail) return null;

        return (
            opticianOptions.find(opt => opt.value === addEyeTestConfig.opticianEmail) || {
                value: addEyeTestConfig.opticianEmail,
                label:
                    addEyeTestConfig?.opticianName ||
                    addEyeTestConfig?.opticianEmail,
            }
        );
    }, [addEyeTestConfig?.opticianEmail, addEyeTestConfig?.opticianName, opticianOptions]);

    // Find selected POS option
    const selectedPosOption = useMemo(() => {
        if (!addEyeTestConfig?.posEmail) return null;
        return posOptions.find(item => item.value === addEyeTestConfig.posEmail) || null;
    }, [addEyeTestConfig?.posEmail, posOptions]);

    return (
        <>
            {view == 'view' ? (
                <>
                    {user?.userType === 'OPTICIAN' &&
                        addEyeTestConfig?.infoOpticianComment && (
                            <Card className="mb-3">
                                <Card.Body>
                                    <Form.Group>
                                        <Form.Label>
                                            {t('commentForOptician')}
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            style={{ height: '80px' }}
                                            value={opticianComment}
                                            readOnly
                                        />
                                        {addEyeTestConfig?.infoOpticianCommentDateTime &&
                                            addEyeTestConfig?.infoOpticianCommentDateTime !==
                                            '01 01 0001' ? (
                                            <p>
                                                {t('date')} :{' '}
                                                {
                                                    addEyeTestConfig?.infoOpticianCommentDateTime
                                                }
                                            </p>
                                        ) : null}
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        )}

                    {(user?.userType === 'BACKOFFICE' ||
                        user?.userType === 'ADMIN') && (
                            <Card className="mb-3">
                                <Card.Body>
                                    <Form.Group>
                                        <Form.Label>
                                            {t('commentForOptician')}
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            style={{ height: '80px' }}
                                            value={opticianComment}
                                            onChange={(e) =>
                                                setOpticianComment(e.target.value)
                                            }
                                            isDisabled={
                                                submitted ||
                                                !addEyeTestConfig?.infoOpticianComment
                                            }
                                        />
                                        {addEyeTestConfig?.infoOpticianCommentDateTime &&
                                            addEyeTestConfig?.infoOpticianCommentDateTime !==
                                            '01 01 0001' ? (
                                            <p>
                                                {t('date')} :{' '}
                                                {
                                                    addEyeTestConfig?.infoOpticianCommentDateTime
                                                }
                                            </p>
                                        ) : null}
                                    </Form.Group>

                                    <Form.Group className="mt-3">
                                        <Form.Label>
                                            {t('Commentaire pour le back-office')}
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            style={{ height: '80px' }}
                                            value={posComment}
                                            onChange={(e) =>
                                                setPosComment(e.target.value)
                                            }
                                            isDisabled={
                                                submitted ||
                                                !addEyeTestConfig?.infoOpticianComment
                                            }
                                        />
                                        {addEyeTestConfig?.infoPOSCommentDateTime &&
                                            addEyeTestConfig?.infoPOSCommentDateTime !==
                                            '01 01 0001' ? (
                                            <p>
                                                {t('date')} :{' '}
                                                {
                                                    addEyeTestConfig?.infoPOSCommentDateTime
                                                }
                                            </p>
                                        ) : null}
                                    </Form.Group>

                                    {!submitted &&
                                        !addEyeTestConfig?.infoOpticianComment && (
                                            <Button
                                                variant="primary"
                                                className="mt-3"
                                                onClick={handleCommentSubmit}
                                            >
                                                {t('Envoyer')}
                                            </Button>
                                        )}
                                </Card.Body>
                            </Card>
                        )}
                </>
            ) : null}
            <Card>
                <Card.Body className="modal_form">
                    <Row>
                        <Col md={view === 'add' ? 7 : 12}>
                            <Row className={view === 'view' ? 'custom_disabled' : ''}>
                                {view === 'view' ? (
                                    <>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Code</Form.Label>
                                                <Form.Control
                                                    name="testUniqueCode"
                                                    type="text"
                                                    value={addEyeTestConfig.testUniqueCode || ''}
                                                    placeholder={t('inputText')}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('firstName')}</Form.Label>
                                                <Form.Control
                                                    name="patientFirstName"
                                                    type="text"
                                                    value={addEyeTestConfig.patientFirstName || ''}
                                                    placeholder={t('inputText')}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('name')}</Form.Label>
                                                <Form.Control
                                                    name="patientLastName"
                                                    type="text"
                                                    value={addEyeTestConfig.patientLastName || ''}
                                                    placeholder={t('inputText')}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('dateofBirth')}</Form.Label>
                                                <Form.Control
                                                    name="patientDOB"
                                                    type="text"
                                                    value={
                                                        addEyeTestConfig.patientDOB
                                                            ? new Date(addEyeTestConfig.patientDOB)
                                                                .toLocaleDateString('en-GB')
                                                                .replace(/\//g, '-')
                                                            : ''
                                                    }
                                                    placeholder={t('inputText')}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('age')}</Form.Label>
                                                <Form.Control
                                                    name="age"
                                                    type="text"
                                                    value={addEyeTestConfig.age || ''}
                                                    placeholder={t('inputText')}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                    </>
                                ) : null}

                                {user?.userType !== 'EYEDOCTOR' && (
                                    <Col md={view === 'add' ? 12 : 6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('optician')}*</Form.Label>
                                            {view === 'view' ? (
                                                <Form.Control
                                                    type="text"
                                                    value={
                                                        addEyeTestConfig?.opticianName ||
                                                        addEyeTestConfig?.opticianEmail ||
                                                        'N/A'
                                                    }
                                                    readOnly
                                                />
                                            ) : (
                                                <>
                                                    {/* Case 1: OPTICIAN user → fixed, disabled */}
                                                    {user?.userType === 'OPTICIAN' ? (
                                                        <Select
                                                            className="custom_select"
                                                            isDisabled={true}
                                                            value={selectedOpticianOption}
                                                            options={[]}
                                                        />
                                                    ) : (
                                                        /* Case 2: ADMIN or BACKOFFICE → selectable */
                                                        <Select
                                                            className="custom_select"
                                                            name="opticianEmail"
                                                            options={opticianOptions}
                                                            placeholder={t('selectOption')}
                                                            value={selectedOpticianOption}
                                                            onChange={(selected) => {
                                                                setAddEyeTestConfig((prev) => ({
                                                                    ...prev,
                                                                    opticianEmail: selected?.value || '',
                                                                    opticianName: selected?.label || '',
                                                                }));
                                                            }}
                                                            isClearable
                                                        />
                                                    )}
                                                </>
                                            )}
                                            {error?.opticianEmail && (
                                                <Form.Control.Feedback type="invalid">
                                                    {t('mustEnterValue')}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                )}

                                {user?.userType !== 'EYEDOCTOR' && (
                                    <Col md={view === 'add' ? 12 : 6}>
                                        <Form.Group className={`mb-3 ${error?.posEmail ? 'error_input' : ''}`}>
                                            <Form.Label>{t('pos')}*</Form.Label>
                                            {view === 'view' ? (
                                                <Form.Control
                                                    type="text"
                                                    value={
                                                        selectedPosOption?.label ||
                                                        addEyeTestConfig?.posEmail ||
                                                        'N/A'
                                                    }
                                                    readOnly
                                                />
                                            ) : (
                                                <Select
                                                    options={posOptions}
                                                    placeholder={t('selectOption')}
                                                    className="custom_select"
                                                    isDisabled={user?.userType === 'OPTICIAN' && user?.isExternal}
                                                    value={selectedPosOption}
                                                    onChange={(selected) => {
                                                        setAddEyeTestConfig(prev => ({
                                                            ...prev,
                                                            posEmail: selected?.value || '',
                                                        }));
                                                    }}
                                                    name="posEmail"
                                                    isClearable={!isExternal}
                                                />
                                            )}
                                            {error?.posEmail && (
                                                <Form.Control.Feedback type="invalid">
                                                    {t('mustEnterValue')}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                )}

                                <Col md={view === 'add' ? 12 : 6}>
                                    <Form.Group
                                        className={`mb-3 ${error?.patientEmail
                                            ? 'error_input'
                                            : ''
                                            }`}
                                    >
                                        <Form.Label>
                                            {t('patientEmail')}*
                                        </Form.Label>
                                        {view === 'view' ? (
                                            <Form.Control
                                                type="text"
                                                value={
                                                    addEyeTestConfig?.patientEmail ||
                                                    'N/A'
                                                }
                                                readOnly
                                            />
                                        ) : (
                                            <AsyncSelect
                                                cacheOptions
                                                defaultOptions={false}
                                                loadOptions={async (
                                                    inputValue
                                                ) => {
                                                    if (!inputValue) return [];

                                                    console.log(
                                                        'input value ==================== ',
                                                        inputValue
                                                    );

                                                    const res = await fetch(
                                                        `${config.API_URL}/patient/NewsearchPatients`,
                                                        {
                                                            method: 'POST',
                                                            headers: {
                                                                'Content-Type':
                                                                    'application/json',
                                                                Authorization: `Bearer ${token}`,
                                                            },
                                                            body: JSON.stringify(
                                                                {
                                                                    email: '',
                                                                    mobile: '',
                                                                    name: '',
                                                                    search: inputValue,
                                                                    latestRecords: 0,
                                                                    pageSize: 50,
                                                                    pageNumber: 1,
                                                                    startDate:
                                                                        '',
                                                                    endDate: '',
                                                                }
                                                            ),
                                                        }
                                                    );

                                                    const data =
                                                        await res.json();

                                                    const list =
                                                        data?.userProfileData ||
                                                        [];

                                                    console.log(
                                                        'patient data ++++++> ',
                                                        list
                                                    );
                                                    console.log(
                                                        'list =========== ',
                                                        list
                                                    );

                                                    return list.map((item) => ({
                                                        value: item.email,
                                                        label: item.email,
                                                        name: item.name,
                                                        lastName: item.lastName,
                                                    }));
                                                }}
                                                className="custom_select"
                                                classNamePrefix="custom_select"
                                                placeholder="Select patient email"
                                                value={
                                                    addEyeTestConfig?.patientEmail
                                                        ? {
                                                            value: addEyeTestConfig?.patientEmail,
                                                            label: addEyeTestConfig?.patientEmail,
                                                        }
                                                        : null
                                                }
                                                onChange={(
                                                    selectedOption,
                                                    actionMeta
                                                ) => {

                                                    console.log(
                                                        'selectedOption ========== ',
                                                        selectedOption
                                                    );
                                                    console.log(
                                                        'actionMeta ========== ',
                                                        actionMeta
                                                    );

                                                    if (
                                                        actionMeta.action ===
                                                        'select-option'
                                                    ) {
                                                        if (
                                                            state.glass ===
                                                            false
                                                        ) {
                                                            fetchSelectedPatientDetails(
                                                                selectedOption?.value
                                                            );
                                                        }

                                                        handleSelectChange(
                                                            selectedOption,
                                                            actionMeta
                                                        );

                                                        console.log(
                                                            '810 -- step1 -- selectedOption?.name',
                                                            selectedOption?.name
                                                        );

                                                        setAddEyeTestConfig(
                                                            (prev) => ({
                                                                ...prev,
                                                                name:
                                                                    selectedOption?.name ||
                                                                    '',
                                                                lastName:
                                                                    selectedOption?.lastName ||
                                                                    '',
                                                            })
                                                        );
                                                    }
                                                }}
                                                name="patientEmail"
                                                isDisabled={view !== 'add' || state?.fromPatient}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    // ✅ Make spinner visible, centered, and slightly darker
                                                    loadingIndicator: (
                                                        base
                                                    ) => ({
                                                        ...base,
                                                        color: '#0d6efd', // Bootstrap blue
                                                        width: 16,
                                                        height: 16,
                                                        marginRight: 8,
                                                        opacity: 0.9,
                                                    }),
                                                }}
                                            />
                                        )}
                                        {error?.patientEmail && (
                                            <Form.Control.Feedback type="invalid">
                                                {t('mustEnterValue')}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>

                                <Col md={view === 'add' ? 12 : 6}>
                                    <Form.Group className={`mb-3 ${error?.lastTimeWentToEyeDoc ? 'error_input' : ''}`}>
                                        <Form.Label>{t('lastVisitToTheOphthalmologist')}*</Form.Label>
                                        {view === 'view' ? (
                                            <Form.Control
                                                type="text"
                                                value={
                                                    addEyeTestConfig.lastTimeWentToEyeDoc
                                                        ? new Date(addEyeTestConfig.lastTimeWentToEyeDoc)
                                                            .toLocaleDateString('en-GB')
                                                            .replace(/\//g, '-')
                                                        : t('neverSeenAnOphthalmologist')
                                                }
                                                readOnly
                                            />
                                        ) : (
                                            <>
                                                <Form.Control
                                                    type="date"
                                                    max={maxDateValidation}
                                                    name="lastTimeWentToEyeDoc"
                                                    value={addEyeTestConfig.lastTimeWentToEyeDoc || ''}
                                                    onChange={(e) => {
                                                        setLastVisit(false);
                                                        handleChange(e);
                                                    }}
                                                />
                                                <Form.Check
                                                    type="checkbox"
                                                    id="never-seen-checkbox"
                                                    className="mt-2"
                                                    checked={addEyeTestConfig.lastTimeWentToEyeDoc === ''}
                                                    onChange={() => {
                                                        setLastVisit(true);
                                                        setAddEyeTestConfig(prev => ({
                                                            ...prev,
                                                            lastTimeWentToEyeDoc: '',
                                                        }));
                                                    }}
                                                    label={t('neverSeenAnOphthalmologist')}
                                                />
                                            </>
                                        )}
                                        {error?.lastTimeWentToEyeDoc && (
                                            <Form.Control.Feedback type="invalid">
                                                {t('whenisthelasttimeyouwenttotheeyedoctor')}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>

                                {/* Show resultExamine field only for non-external users */}
                                {!isExternal && (user?.userType === 'BACKOFFICE' || user?.userType === 'ADMIN') && (
                                    <Col md={12}>
                                        <Form.Group className={`mb-3 ${error?.resultExamine ? 'error_input' : ''}`}>
                                            <Form.Label>{t('resultExamineTitle')}*</Form.Label>
                                            {view === 'view' ? (
                                                <Form.Control
                                                    type="text"
                                                    value={addEyeTestConfig?.infoResultExamine || 'N/A'}
                                                    readOnly
                                                />
                                            ) : (
                                                <Form.Select
                                                    value={
                                                        addEyeTestConfig?.resultExamine ||
                                                        addEyeTestConfig?.infoResultExamine ||
                                                        ''
                                                    }
                                                    name="resultExamine"
                                                    onChange={handleChange}
                                                    aria-label="Default select example"
                                                    required
                                                >
                                                    <option disabled value="">{t('selectItem')}</option>
                                                    <option value="Devis">{t('devis')}</option>
                                                    <option value="Juste ordonnance">{t('justeOrdonnance')}</option>
                                                    <option value="Vente">{t('vente')}</option>
                                                    <option value="Retour ophtalmologue">{t('retourOphtalmologue')}</option>
                                                    <option value="Sans ordonnance">{t('sansOrdonnance')}</option>
                                                </Form.Select>
                                            )}
                                            {error?.resultExamine && (
                                                <Form.Control.Feedback type="invalid">
                                                    {t('mustEnterValue')}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>

                            {/* Old Lens Prescription Image Upload */}
                            <Row>
                                {(eyeTestImages.eyeTestImagePath1 || view === 'add') && !glass && (
                                    <Col md={12} className="mb-3">
                                        <Card className="add_eye_upload">
                                            <Card.Title>{t('oldLensPrescription')}</Card.Title>

                                            <Card.Body>
                                                <Stack gap={3}>
                                                    {eyeTestImages.eyeTestImagePath1 ? (
                                                        (() => {
                                                            const fileName = eyeTestImages.eyeTestImagePath1;
                                                            const fileUrl = `${config.img_URL}/${fileName}`;
                                                            const encodedUrl = encodeURIComponent(fileUrl);

                                                            const isPdf = fileName.toLowerCase().endsWith('.pdf');
                                                            const isHeic =
                                                                fileName.toLowerCase().endsWith('.heic') ||
                                                                fileName.toLowerCase().endsWith('.heif');

                                                            const handleFileOpen = (e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();

                                                                /* ======================= PDF (GOOGLE DOCS) ======================= */
                                                                if (isPdf) {
                                                                    const googleViewerUrl =
                                                                        `https://docs.google.com/viewer?url=${encodedUrl}&embedded=false`;

                                                                    window.open(googleViewerUrl, '_blank');
                                                                    return;
                                                                }

                                                                /* ======================= HEIC ======================= */
                                                                if (isHeic) {
                                                                    const a = document.createElement('a');
                                                                    a.href = fileUrl;
                                                                    a.download = fileName;
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    document.body.removeChild(a);
                                                                    return;
                                                                }

                                                                /* ======================= IMAGE ======================= */
                                                                const win = window.open('_blank');
                                                                if (!win) return;

                                                                win.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>${fileName}</title>
  <style>
    body {
      margin: 0;
      background: #111;
      display: flex;
      flex-direction: column;
      height: 100vh;
    }
    .toolbar {
      height: 50px;
      background: rgba(0,0,0,0.85);
      color: #fff;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 12px;
    }
    img {
      max-width: 100%;
      max-height: calc(100vh - 50px);
      margin: auto;
      object-fit: contain;
    }
    .btn {
      background: #007bff;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      text-decoration: none;
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <span>${fileName}</span>
    <a class="btn" href="${fileUrl}" download>⬇ Télécharger</a>
  </div>
  <img src="${fileUrl}" />
</body>
</html>
                  `);
                                                                win.document.close();
                                                            };

                                                            return (
                                                                <div
                                                                    onClick={handleFileOpen}
                                                                    style={{ cursor: 'pointer', textAlign: 'center' }}
                                                                >
                                                                    {/* ======================= PDF PREVIEW ======================= */}
                                                                    {isPdf && (
                                                                        <div>
                                                                            <div
                                                                                style={{
                                                                                    width: 120,
                                                                                    height: 160,
                                                                                    margin: '0 auto',
                                                                                    background: '#f8f9fa',
                                                                                    border: '2px dashed #dee2e6',
                                                                                    borderRadius: 8,
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                            >
                                                                                <FileText size={60} color="#e74c3c" />
                                                                                <div style={{ fontSize: 12, marginTop: 10 }}>
                                                                                    PDF
                                                                                </div>
                                                                            </div>
                                                                            <small className="text-primary mt-2 d-block">
                                                                                Ouvrir fichier
                                                                            </small>
                                                                        </div>
                                                                    )}

                                                                    {/* ======================= HEIC PREVIEW ======================= */}
                                                                    {isHeic && (
                                                                        <div>
                                                                            <FileText size={60} color="#3498db" />
                                                                            <small className="text-primary d-block mt-2">
                                                                                ⬇ Télécharger
                                                                            </small>
                                                                        </div>
                                                                    )}

                                                                    {/* ======================= IMAGE PREVIEW ======================= */}
                                                                    {!isPdf && !isHeic && (
                                                                        <>
                                                                            <Image
                                                                                src={fileUrl}
                                                                                style={{
                                                                                    maxWidth: '100%',
                                                                                    maxHeight: 300,
                                                                                    objectFit: 'contain',
                                                                                    border: '1px solid #ddd',
                                                                                    borderRadius: 5,
                                                                                    padding: 5,
                                                                                    background: '#f8f9fa'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src = EyeUpload;
                                                                                    e.target.style.opacity = 0.5;
                                                                                }}
                                                                            />
                                                                            <small className="text-primary d-block mt-2">
                                                                                Ouvrir fichier
                                                                            </small>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()
                                                    ) : (
                                                        <Image src={EyeUpload} style={{ maxWidth: '100%', opacity: 0.5 }} />
                                                    )}

                                                    {view !== 'view' && (
                                                        <Form.Control
                                                            type="file"
                                                            name="image1"
                                                            accept="image/*,.heic,.heif,application/pdf"
                                                            onChange={imageHandle}
                                                            required={view === 'add' && !images.image1}
                                                        />
                                                    )}
                                                </Stack>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Age Restriction Modal */}
            {/* <Modal
                show={modalActive}
                backdrop="static"
                keyboard={false}
                onHide={() => setModalActive(false)}
                centered
            >
                <Modal.Body className="text-center">
                    <CircleAlert size={60} className="mb-4 mt-4 text-warning" />
                    <h5 className="mb-3">{t('attention')}</h5>
                    <p className="mb-4">
                        {t('Les tests de lentilles ne sont pas autorisés pour les patients de moins de 18 ans.')}
                    </p>
                    <Button
                        variant="primary"
                        onClick={() => {
                            setModalActive(false);
                            // Navigate back or clear selection
                            if (addEyeTestConfigRef.current?.patientEmail) {
                                setAddEyeTestConfig(prev => ({
                                    ...prev,
                                    patientEmail: '',
                                    name: '',
                                    lastName: '',
                                }));
                            }
                        }}
                    >
                        {t('okay')}
                    </Button>
                </Modal.Body>
            </Modal> */}
        </>
    );
};

export default Step1;